/**
 * Размещение менеджера: чистые помощники экрана (ядро S2).
 *
 * Площадь вводится в гектарах (с запятой или точкой), хранится в микрогектарах:
 * целое число, без плавающей арифметики (как весь канон).
 */

import { defineMessages } from "../../i18n/define";
import { tr, numberLocale } from "../../i18n/runtime";
import { missingLabel } from "../../lib/format";

export const MICROHA = 1_000_000;

/** «7,5» / «7.5» → микрогектары; пусто, ноль и мусор — `null` (отказывает форма, не сервер). */
export function parseHa(text: string): number | null {
  const clean = text.trim().replace(/\s/g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,6})?$/.test(clean)) return null;
  const value = Math.round(Number(clean) * MICROHA);
  return value > 0 ? value : null;
}

export function formatHa(microha: number | null | undefined): string {
  if (microha === null || microha === undefined) return missingLabel();
  const value = microha / MICROHA;
  // Малая площадь (0,0033 га) не должна округляться до «0»: это не пустая строка, а сотки.
  return value.toLocaleString(numberLocale(), {
    maximumFractionDigits: value > 0 && value < 0.1 ? 4 : 2,
  });
}

const STATUS = defineMessages({
  new: { id: "placement.importStatus.new", defaultMessage: "будет внесена", description: "Статус строки файла размещения в предпросмотре импорта (менеджер): строка новая и будет внесена при фиксации; строчная" },
  correction: { id: "placement.importStatus.correction", defaultMessage: "исправит внесённую", description: "Статус строки файла размещения: исправит ранее внесённую строку; строчная" },
  unchanged: { id: "placement.importStatus.unchanged", defaultMessage: "уже внесена", description: "Статус строки файла размещения: такая строка уже есть, ничего не изменится; строчная" },
  unmatched: { id: "placement.importStatus.unmatched", defaultMessage: "не сопоставлена", description: "Статус строки файла размещения: контур не найден в реестре; строчная" },
  invalid: { id: "placement.importStatus.invalid", defaultMessage: "ошибка значений", description: "Статус строки файла размещения: в строке недопустимые значения (площадь, дата); строчная" },
  conflict: { id: "placement.importStatus.conflict", defaultMessage: "конфликт", description: "Статус строки файла размещения: противоречит уже внесённым данным; строчная" },
  duplicate_in_file: { id: "placement.importStatus.duplicate_in_file", defaultMessage: "повтор в файле", description: "Статус строки файла размещения: та же строка встречается в файле дважды; строчная" },
});

/** Порядок статусов в счётчиках предпросмотра: что произойдёт со строками. */
export const IMPORT_STATUS_ORDER = [
  "new",
  "correction",
  "unchanged",
  "unmatched",
  "invalid",
  "conflict",
  "duplicate_in_file",
] as const;

/** Подпись статуса строки файла; незнакомый статус показывается как есть. */
export function importStatusLabel(code: string): string {
  return Object.prototype.hasOwnProperty.call(STATUS, code)
    ? tr(STATUS[code as keyof typeof STATUS])
    : code;
}

/** Строки с этими статусами вносятся при фиксации; остальные остаются видимыми. */
export const COMMITTABLE = new Set(["new", "correction"]);

const WARNING = defineMessages({
  actor_mismatch: { id: "placement.warning.actorMismatch", defaultMessage: "хозяйство ≠ землепользователь", description: "Пометка у строки размещения (менеджер): указанное хозяйство не совпадает с землепользователем контура; узкая колонка, коротко" },
  no_land_user: { id: "placement.warning.noLandUser", defaultMessage: "нет землепользователя", description: "Пометка у строки размещения: у контура не названо землепользование на дату; узкая колонка, коротко" },
  actor_unmatched: { id: "placement.warning.actorUnmatched", defaultMessage: "хозяйство не сопоставлено", description: "Пометка у строки размещения: хозяйство из файла не найдено в реестре; узкая колонка, коротко" },
});

/** Подпись предупреждения о расхождении размещения и землепользования. */
export function warningLabel(code: string): string {
  return Object.prototype.hasOwnProperty.call(WARNING, code)
    ? tr(WARNING[code as keyof typeof WARNING])
    : code;
}

export function fieldLabel(row: { contour_no: string; part_label?: string | null }): string {
  return row.part_label ? `${row.contour_no} / ${row.part_label}` : row.contour_no;
}
